import {
    IonAlert,
    IonButton,
    IonContent,
    IonHeader,
    IonIcon,
    IonPage,
    IonProgressBar,
    IonRefresher,
    IonRefresherContent,
    IonSpinner,
    IonTitle,
    IonToolbar,
    IonToast,
    RefresherEventDetail,
    useIonRouter
} from "@ionic/react";

import {
    arrowForwardOutline,
    checkmarkOutline,
    closeOutline,
    copyOutline,
    peopleOutline,
    personOutline,
    refreshOutline,
    settingsOutline,
    shieldCheckmarkOutline,
    swapHorizontalOutline,
    trophyOutline,
    calendarOutline,
    trashOutline
} from "ionicons/icons";

import { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import Cookies from "js-cookie";
import "./fantasySettings.css";

function getLeagueName(data: unknown): string {
    if (!data || typeof data !== "object") {
        return "Configuración";
    }

    const rootValues = Object.values(data).filter(Boolean);
    const firstLeagueData = rootValues[0];

    if (!firstLeagueData || typeof firstLeagueData !== "object") {
        return "Configuración";
    }

    /*
        Se conserva la estructura previa, donde info[2]
        contenía el nombre de la liga.
    */
    const leagueInfo = Object.values(firstLeagueData).filter(Boolean);
    const possibleName = leagueInfo[2];

    if (
        typeof possibleName === "string" &&
        possibleName.trim().length > 0
    ) {
        return possibleName;
    }

    return "Liga Fantasy";
}

function getUserRole(data: unknown, userId: string): string {
    if (!data || typeof data !== "object" || !userId) {
        return "";
    }

    const league = data as {
        Teams?: Record<
            string,
            {
                user?: string;
            }
        >;
    };

    return league.Teams?.[userId]?.user || "";
}

const FantasySettingsMenu: React.FC = () => {
    const router = useIonRouter();
    const { id } = useParams<{ id: string }>();
    const history = useHistory();

function goToLeagueHome() {
    history.replace(`/fantasyLeague/${id}`);
}

    const [leagueName, setLeagueName] = useState("Liga Fantasy");
    const [userRole, setUserRole] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [toastMessage, setToastMessage] = useState("");
const [leagueIdCopied, setLeagueIdCopied] = useState(false);
const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
const [leaguePassword, setLeaguePassword] = useState("");
const [deletingLeague, setDeletingLeague] = useState(false);

    function getAccessToken(): string {
        return Cookies.get("access_token") || "";
    }

function isAuthenticated(): boolean {
    return Boolean(
        Cookies.get("access_token") &&
        Cookies.get("access_username")
    );
}


    const loadLeagueSettings = useCallback(
        async function (forceRefresh = false) {
            if (!isAuthenticated()) {
                router.push("/home", "root", "replace");
                return;
            }

            try {
                setError("");

                if (!forceRefresh) {
                    setLoading(true);
                }

                const dataRoot = process.env.PUBLIC_URL || "";

                const response = await fetch(
                    `${dataRoot}/fantasy_data/${id}.json?t=${Date.now()}`,
                    {
                        cache: "no-store"
                    }
                );

                if (!response.ok) {
                    throw new Error(
                        `No fue posible cargar la configuración. Código: ${response.status}`
                    );
                }

                const data = await response.json();

                setLeagueName(getLeagueName(data));
                setUserRole(getUserRole(data, getAccessToken()));
            } catch (requestError) {
                console.error(
                    "No fue posible cargar la configuración:",
                    requestError
                );

                setError(
                    "No fue posible cargar las opciones de esta liga."
                );
            } finally {
                setLoading(false);
            }
        },
        [id, router]
    );

    useEffect(
        function () {
            loadLeagueSettings();
        },
        [loadLeagueSettings]
    );

    async function handleRefresh(
        event: CustomEvent<RefresherEventDetail>
    ) {
        await loadLeagueSettings(true);
        event.detail.complete();
    }

    async function copyLeagueId() {
    try {
        if (
            navigator.clipboard &&
            typeof navigator.clipboard.writeText === "function"
        ) {
            await navigator.clipboard.writeText(id);
        } else {
            /*
                Respaldo para navegadores o WebViews antiguos.
            */
            const textArea = document.createElement("textarea");

            textArea.value = id;
            textArea.style.position = "fixed";
            textArea.style.opacity = "0";

            document.body.appendChild(textArea);
            textArea.select();

            const copied = document.execCommand("copy");

            document.body.removeChild(textArea);

            if (!copied) {
                throw new Error("No fue posible copiar el ID.");
            }
        }

        setLeagueIdCopied(true);
        setToastMessage("ID de liga copiado al portapapeles.");

        window.setTimeout(function () {
            setLeagueIdCopied(false);
        }, 2200);
    } catch (copyError) {
        console.error(
            "No fue posible copiar el ID de liga:",
            copyError
        );

        setToastMessage(
            "No fue posible copiar el ID. Intenta seleccionarlo manualmente."
        );
    }
}

async function deleteLeagueWithPassword(password: string) {
    try {
        setDeletingLeague(true);

        const dataRoot = process.env.PUBLIC_URL || "";

        const response = await fetch(
            `${process.env.PUBLIC_URL || ""}/control_deleteLeague.php`,
            {
                method: "DELETE",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                    "X-Access-Username": Cookies.get("access_username") || "",
                    "X-Access-Token": getAccessToken()
                },
                body: JSON.stringify({
                    leagueId: id,
                    password
                })
            }
        );

        const result = await response.json().catch(function () {
            return {};
        });

        if (!response.ok) {
            throw new Error(
                result.message ||
                    "No fue posible eliminar la liga."
            );
        }

        setLeaguePassword("");
        setToastMessage("La liga fue eliminada correctamente.");

        window.setTimeout(function () {
            router.push("/fantasy", "root", "replace");
        }, 700);
    } catch (deleteError) {
        console.error("No fue posible eliminar la liga:", deleteError);

        setToastMessage(
            deleteError instanceof Error
                ? deleteError.message
                : "No fue posible eliminar la liga."
        );
    } finally {
        setDeletingLeague(false);
    }
}

async function deleteLeague() {
    if (!leaguePassword.trim()) {
        setToastMessage("Ingresa la contraseña de la liga.");
        return;
    }

    try {
        setDeletingLeague(true);

        const dataRoot = process.env.PUBLIC_URL || "";

        const response = await fetch(
            `${dataRoot}/api/fantasy/leagues/${encodeURIComponent(id)}`,
            {
                method: "DELETE",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${getAccessToken()}`
                },
                body: JSON.stringify({
                    password: leaguePassword
                })
            }
        );

        const result = await response.json().catch(function () {
            return {};
        });

        if (!response.ok) {
            throw new Error(
                result.message ||
                    "No fue posible eliminar la liga."
            );
        }

        setLeaguePassword("");
        setToastMessage("La liga fue eliminada correctamente.");

        window.setTimeout(function () {
            router.push("/fantasy", "root", "replace");
        }, 700);
    } catch (deleteError) {
        console.error("No fue posible eliminar la liga:", deleteError);

        setToastMessage(
            deleteError instanceof Error
                ? deleteError.message
                : "No fue posible eliminar la liga."
        );
    } finally {
        setDeletingLeague(false);
    }
}

    const isCommissioner =
        userRole.toLowerCase() === "comisioner" ||
        userRole.toLowerCase() === "commissioner";

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar className="settings-toolbar">
                    <IonButton
    slot="start"
    fill="clear"
    color="medium"
    aria-label="Regresar a la liga"
    onClick={goToLeagueHome}
>
    <IonIcon icon={closeOutline} />
</IonButton>


                    <IonTitle>Configuración</IonTitle>
                </IonToolbar>
            </IonHeader>

            <IonContent fullscreen className="settings-content">
                <IonRefresher
                    slot="fixed"
                    onIonRefresh={handleRefresh}
                >
                    <IonRefresherContent
                        pullingText="Desliza para actualizar"
                        refreshingText="Actualizando configuración..."
                    />
                </IonRefresher>

                <section className="settings-hero">
                    <div className="settings-hero__icon">
                        <IonIcon icon={settingsOutline} />
                    </div>

                    <div>
                        <span>CONFIGURACIÓN DE LIGA</span>
                        <h1>{leagueName}</h1>
                        <p>
                            Gestiona tu equipo y las reglas de la competencia.
                        </p>
                    </div>
                </section>

                {loading && (
                    <IonProgressBar
                        className="settings-progress"
                        type="indeterminate"
                    />
                )}

                {!loading && error && (
                    <main className="settings-main">
                        <div className="settings-empty">
                            <IonIcon icon={refreshOutline} />
                            <strong>No pudimos cargar el menú</strong>
                            <span>{error}</span>

                            <IonButton
                                fill="outline"
                                size="small"
                                onClick={function () {
                                    loadLeagueSettings(true);
                                }}
                            >
                                Reintentar
                            </IonButton>
                        </div>
                    </main>
                )}

                {!loading && !error && (
                    <main className="settings-main">
                        <section className="settings-intro">
                            <span>OPCIONES DISPONIBLES</span>
                            <h2>Administra tu liga</h2>
                            <p>
                                Selecciona una sección para actualizar la
                                configuración correspondiente.
                            </p>
                        </section>
                        <section className="settings-invite">
    <div className="settings-invite__icon">
        <IonIcon icon={peopleOutline} />
    </div>

    <div className="settings-invite__content">
        <span>INVITA A OTROS MANAGERS</span>
        <h2>Comparte el ID de tu liga</h2>
        <p>
            Envía este identificador a las personas que deseen
            unirse a la competencia.
        </p>

        <div className="settings-invite__id">
            <code>{id}</code>

            <button
                type="button"
                className="settings-invite__copy"
                aria-label="Copiar ID de liga"
                onClick={copyLeagueId}
            >
                <IonIcon
                    icon={
                        leagueIdCopied
                            ? checkmarkOutline
                            : copyOutline
                    }
                />

                <span>
                    {leagueIdCopied ? "Copiado" : "Copiar"}
                </span>
            </button>
        </div>
    </div>
</section>


                        <section className="settings-list">
                            <button
                                className="settings-option settings-option--primary"
                                type="button"
                                onClick={function () {
                                    router.push(
                                        `/teamSettings/${id}`,
                                        "forward",
                                        "push"
                                    );
                                }}
                            >
                                <span className="settings-option__icon">
                                    <IonIcon icon={personOutline} />
                                </span>

                                <span className="settings-option__content">
                                    <strong>Mi equipo</strong>
                                    <small>
                                        Edita el nombre y los ajustes de tu
                                        equipo.
                                    </small>
                                </span>

                                <IonIcon
                                    className="settings-option__arrow"
                                    icon={arrowForwardOutline}
                                />
                            </button>

                            <button
                                className="settings-option"
                                type="button"
                                onClick={function () {
                                    router.push(
                                        `/fantasyPointSettings/${id}`,
                                        "forward",
                                        "push"
                                    );
                                }}
                            >
                                <span className="settings-option__icon">
                                    <IonIcon icon={trophyOutline} />
                                </span>

                                <span className="settings-option__content">
                                    <strong>Puntuación</strong>
                                    <small>
                                        Consulta las reglas de puntos de la
                                        liga.
                                    </small>
                                </span>

                                <IonIcon
                                    className="settings-option__arrow"
                                    icon={arrowForwardOutline}
                                />
                            </button>
                        </section>

                        {isCommissioner && (
                            <section className="settings-admin">
                                <div className="settings-admin__heading">
                                    <span className="settings-admin__icon">
                                        <IonIcon
                                            icon={shieldCheckmarkOutline}
                                        />
                                    </span>

                                    <div>
                                        <span>COMISIONADO</span>
                                        <h2>Administración de liga</h2>
                                    </div>
                                </div>

                                <button
                                    className="settings-option"
                                    type="button"
                                    onClick={function () {
                                        router.push(
                                            `/leagueSettings/${id}`,
                                            "forward",
                                            "push"
                                        );
                                    }}
                                >
                                    <span className="settings-option__icon">
                                        <IonIcon icon={peopleOutline} />
                                    </span>

                                    <span className="settings-option__content">
                                        <strong>Editar liga</strong>
                                        <small>
                                            Modifica reglas, participantes y
                                            ajustes generales.
                                        </small>
                                    </span>

                                    <IonIcon
                                        className="settings-option__arrow"
                                        icon={arrowForwardOutline}
                                    />
                                </button>
                                
                                <button
                                    className="settings-option settings-option--draft"
                                    type="button"
                                    onClick={function () {
                                        router.push(
                                            `/fantasyDraftSettings/${id}`,
                                            "forward",
                                            "push"
                                        );
                                    }}
                                >
                                    <span className="settings-option__icon">
                                        <IonIcon icon={calendarOutline} />
                                    </span>

                                    <span className="settings-option__content">
                                        <strong>Configuración del draft</strong>

                                        <small>
                                            Edita fecha, hora, modalidad y orden de selección.
                                        </small>
                                    </span>

                                    <IonIcon
                                        className="settings-option__arrow"
                                        icon={arrowForwardOutline}
                                    />
                                </button>

                                <button
                                    className="settings-option settings-option--commissioner"
                                    type="button"
                                    onClick={function () {
                                        router.push(
                                            `/commissionerTeamSettings/${id}`,
                                            "forward",
                                            "push"
                                        );
                                    }}
                                >
                                    <span className="settings-option__icon">
                                        <IonIcon icon={swapHorizontalOutline} />
                                    </span>

                                    <span className="settings-option__content">
                                        <strong>Editar equipos</strong>
                                        <small>
                                            Cambia nombres o reemplaza un equipo sin perder su estado.
                                        </small>
                                    </span>

                                    <IonIcon
                                        className="settings-option__arrow"
                                        icon={arrowForwardOutline}
                                    />
                                </button>

                                <button
                                    className="settings-option settings-option--trades"
                                    type="button"
                                    onClick={function () {
                                        router.push(
                                            `/commissionerTrades/${id}`,
                                            "forward",
                                            "push"
                                        );
                                    }}
                                >
                                    <span className="settings-option__icon">
                                        <IonIcon icon={swapHorizontalOutline} />
                                    </span>

                                    <span className="settings-option__content">
                                        <strong>Control de intercambios</strong>

                                        <small>
                                            Acepta, cancela o revierte intercambios de la liga.
                                        </small>
                                    </span>

                                    <IonIcon
                                        className="settings-option__arrow"
                                        icon={arrowForwardOutline}
                                    />
                                </button>
                                <button
                                    className="settings-option settings-option--schedule"
                                    type="button"
                                    onClick={function () {
                                        router.push(
                                            `/fantasyScheduleSettings/${id}`,
                                            "forward",
                                            "push"
                                        );
                                    }}
                                >
                                    <span className="settings-option__icon">
                                        <IonIcon icon={calendarOutline} />
                                    </span>

                                    <span className="settings-option__content">
                                        <strong>Calendario de liga</strong>

                                        <small>
                                            Ajusta semanas, playoffs, enfrentamientos y jornadas de descanso.
                                        </small>
                                    </span>

                                    <IonIcon
                                        className="settings-option__arrow"
                                        icon={arrowForwardOutline}
                                    />
                                </button>

<button
    className="settings-option settings-option--danger"
    type="button"
    disabled={deletingLeague}
    onClick={function () {
        setShowDeleteConfirm(true);
    }}
>
    <span className="settings-option__icon">
        {deletingLeague ? (
            <IonSpinner name="crescent" />
        ) : (
            <IonIcon icon={trashOutline} />
        )}
    </span>

    <span className="settings-option__content">
        <strong>
            {deletingLeague ? "Eliminando liga..." : "Eliminar liga"}
        </strong>
        <small>
            Elimina permanentemente la liga y la desvincula de todos los participantes.
        </small>
    </span>
</button>
                            </section>
                        )}
                    </main>
                )}
            </IonContent>
            <IonToast
    isOpen={Boolean(toastMessage)}
    message={toastMessage}
    duration={2600}
    position="bottom"
    onDidDismiss={function () {
        setToastMessage("");
    }}
/>
<IonAlert
    isOpen={showDeleteConfirm}
    header="¿Eliminar liga?"
    message={`¿Estás seguro de querer eliminar la liga "${leagueName}"? Esta acción no se puede deshacer.`}
    onDidDismiss={function () {
        setShowDeleteConfirm(false);
    }}
    buttons={[
        {
            text: "Cancelar",
            role: "cancel"
        },
        {
            text: "Aceptar",
            role: "destructive",
            handler: function () {
                setShowPasswordPrompt(true);
            }
        }
    ]}
/>

<IonAlert
    isOpen={showPasswordPrompt}
    header="Confirma la contraseña"
    message="Ingresa la contraseña de la liga para eliminarla permanentemente."
    onDidDismiss={function () {
        setShowPasswordPrompt(false);
        setLeaguePassword("");
    }}
    inputs={[
        {
            name: "password",
            type: "password",
            placeholder: "Contraseña de la liga",
            attributes: {
                autocomplete: "current-password"
            }
        }
    ]}
    buttons={[
        {
            text: "Cancelar",
            role: "cancel"
        },
        {
            text: "Eliminar",
            role: "destructive",
            handler: function (values) {
                const password = String(values.password || "");

                if (!password.trim()) {
                    setToastMessage("Ingresa la contraseña de la liga.");
                    return false;
                }

                setLeaguePassword(password);
                setTimeout(function () {
                    deleteLeagueWithPassword(password);
                }, 0);

                return true;
            }
        }
    ]}
/>

        </IonPage>
    );
};

export default FantasySettingsMenu;
